import { useEffect, useState } from 'react'
import { logError } from '@/utils/errorUtils'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useMedplum } from '@medplum/react'
import type { Coverage, CoverageEligibilityResponse } from '@medplum/fhirtypes'
import { IconAlertCircle, IconCheck } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks'
import { CustomInsurancePayerModal } from '@/components/ui/CustomInsurancePayerModal'
import { ImageZoomModal } from '@/components/ui/ImageZoomModal'
import type {
  CoverageTemplate,
  InsuranceCardSide,
  InsuranceFormData,
  InsurancePlanSnapshot,
  InsuranceRank,
  LocalCoverage,
} from '@/types/insuranceTypes'
import {
  buildCoverageTemplateFromValues,
  getCoveragePlanSnapshot,
  getCoverageState,
  getOrderFromRank,
  getSubscriberDob,
  getSubscriberMemberId,
  getSubscriberName,
  selectCoverageForOrder,
  validateNameField,
} from '@/utils/coverageUtils'
import { ManageCoverageCard } from './ManageCoverageCard'
import { ManageCoveragesEditorModal } from './ManageCoveragesEditorModal'
import type { EditableCoverage, ManageCoveragesProps } from './manageCoverages.types'
import { useCoveragePersistence } from './useCoveragePersistence'
import { useDeferredCoverageDrafts } from './useDeferredCoverageDrafts'
import { useInsuranceCardState } from './useInsuranceCardState'
import { INSURANCE_RANKS } from '@/constants/insuranceConstants'
import {
  closePatientInsuranceRequest,
  fetchLatestPatientInsuranceRequest,
  type PatientInsuranceRequest,
} from '@/utils/insuranceQuestionnaire'

export function ManageCoverages({
  patientId,
  initialInsuranceRank,
  onCoverageSaved,
  onPatientInsuranceRequestDismissed,
  onInsuranceModalClose,
  registerSaveCoverages,
}: ManageCoveragesProps) {
  const medplum = useMedplum()
  const middlewareClient = useMiddlewareClient()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCoverage, setEditingCoverage] = useState<EditableCoverage>(null)
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [isCreatePayerModalOpen, setIsCreatePayerModalOpen] = useState(false)
  const [isEligibilityCheck, setIsEligibilityCheck] = useState(false)
  const [latestEligibilityResponse, setLatestEligibilityResponse] =
    useState<CoverageEligibilityResponse | null>(null)
  const [isEligibilityStale, setIsEligibilityStale] = useState(false)
  const [activeInsuranceRank, setActiveInsuranceRank] = useState<InsuranceRank | null>(null)
  const [patientInsuranceRequest, setPatientInsuranceRequest] =
    useState<PatientInsuranceRequest | null>(null)
  const COVERAGE_VERSION_TAG_SYSTEM = 'https://behuman.health/coverage-version-id'

  const form = useForm<InsuranceFormData>({
    initialValues: {
      memberId: '',
      groupId: '',
      insuranceRank: 'Primary',
      coverageType: '',
      payorId: '',
      payorName: '',
      coverageState: '',
      planName: '',
      productType: '',
      planId: '',
      lobCategory: '',
      subscriberName: '',
      subscriberDob: '',
      subscriberMemberId: '',
    },
    validate: {
      coverageType: (value) => (!value ? 'Coverage type is required' : null),
      insuranceRank: (value) => (!value ? 'Insurance rank is required' : null),
      payorId: (value) => (!value ? 'Payor selection is required' : null),
      subscriberName: (value, values) => {
        if (values.coverageType && values.coverageType.toLowerCase() !== 'self') {
          if (!value || value.trim().length === 0) {
            return 'Subscriber name is required'
          }
          return validateNameField(value)
        }
        return null
      },
      subscriberMemberId: (value, values) => {
        if (values.coverageType && values.coverageType.toLowerCase() !== 'self') {
          if (!value || value.trim().length === 0) {
            return 'Subscriber member ID is required'
          }
        }
        return null
      },
      subscriberDob: (value, values) => {
        if (values.coverageType && values.coverageType.toLowerCase() !== 'self') {
          if (!value) {
            return 'Subscriber date of birth is required'
          }

          const dateRegex = /^(1[89]\d{2}|2\d{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/
          if (!dateRegex.test(value)) {
            return 'Date must be in MM/DD/YYYY format'
          }

          const [year, month, day] = value.split('-').map(Number)
          const date = new Date(year, month - 1, day)

          if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
          ) {
            return 'Please enter a valid date'
          }

          if (date > new Date()) {
            return 'Date of birth cannot be in the future'
          }

          const age = new Date().getFullYear() - year
          if (age > 120) {
            return 'Date of birth seems unrealistic'
          }
        }
        return null
      },
    },
  })

  const insuranceCards = useInsuranceCardState()
  const persistence = useCoveragePersistence({
    medplum,
    middlewareClient,
    patientId,
  })
  const {
    checkInsurance,
    deleteCoverage,
    deleteInsuranceCardImage,
    ensurePatientResource,
    existingCoverages,
    fetchCoverages,
    fetchLatestEligibilityResponseForCoverage,
    fetchOrganizations,
    getInsuranceDisplayData,
    hasFetchedCoverages,
    organizations,
    setPatientResource,
    upsertCoverageForPatient,
    uploadInsuranceCardsForCoverage,
  } = persistence
  const drafts = useDeferredCoverageDrafts({
    onCoverageSaved,
    ensurePatientResource,
    fetchCoverages,
    setPatientResource,
    upsertCoverageForPatient,
    uploadInsuranceCardsForCoverage,
    base64ToFile: insuranceCards.base64ToFile,
    validateCardSelection: insuranceCards.validateCardSelection,
  })

  const closeEditor = () => {
    setIsModalOpen(false)
    form.reset()
    setEditingCoverage(null)
    setSearchValue('')
    setLatestEligibilityResponse(null)
    setIsEligibilityStale(false)
    insuranceCards.resetInsuranceUploads()
    setActiveInsuranceRank(null)
    onInsuranceModalClose?.()
  }

  const updateEligibilityState = (
    coverage: Coverage | null,
    response: CoverageEligibilityResponse | null,
  ) => {
    setLatestEligibilityResponse(response)

    const responseCoverageVersion = response?.meta?.tag?.find(
      (tag) => tag.system === COVERAGE_VERSION_TAG_SYSTEM,
    )?.code

    setIsEligibilityStale(
      Boolean(
        response &&
        coverage?.meta?.versionId &&
        responseCoverageVersion !== coverage.meta.versionId,
      ),
    )
  }

  const populateFormForDraft = (draft: CoverageTemplate) => {
    form.setValues({
      memberId: draft.memberId,
      groupId: draft.groupId || '',
      insuranceRank: draft.insuranceRank,
      coverageType: draft.coverageType,
      payorId: draft.payorId,
      payorName: draft.payorName,
      coverageState: draft.coverageState || '',
      planName: draft.planName || '',
      productType: draft.productType || '',
      planId: draft.planId || '',
      lobCategory: draft.lobCategory || '',
      subscriberName: draft.subscriberName || '',
      subscriberDob: draft.subscriberDob || '',
      subscriberMemberId: draft.subscriberMemberId || '',
    })
    const matchedOrganization = organizations.find(
      (organization) =>
        organization.payerId === draft.payorId && organization.state === draft.coverageState,
    )
    setSearchValue(matchedOrganization?.label || draft.payorName)
    insuranceCards.populateFromDraft(draft)
  }

  const MEMBER_ID_SYSTEM = 'https://behuman.health/fhir/coverage-member-id'

  const populateFormForCoverage = (coverage: Coverage, rank: InsuranceRank) => {
    const memberIdValue =
      coverage.identifier?.find((id) => id.system === MEMBER_ID_SYSTEM)?.value ||
      coverage.identifier?.find((id) => !id.system)?.value ||
      ''
    const memberId = memberIdValue.length > 30 ? '' : memberIdValue
    const groupId = coverage.class?.find((entry) =>
      entry.type?.coding?.some((coding) => coding.code === 'group'),
    )?.value
    const relationshipCode = coverage.relationship?.coding?.[0]?.code
    const normalizedCoverageType =
      relationshipCode === '18'
        ? 'self'
        : relationshipCode === 'G8'
          ? 'other'
          : relationshipCode || ''
    const coverageState = getCoverageState(coverage)
    const planSnapshot = getCoveragePlanSnapshot(coverage)

    form.setValues({
      memberId,
      groupId: groupId || '',
      insuranceRank: rank,
      coverageType: normalizedCoverageType,
      payorId: coverage.payor?.[0]?.identifier?.value || '',
      payorName: coverage.payor?.[0]?.display || '',
      coverageState: coverageState || '',
      planName: planSnapshot?.planName || '',
      productType: planSnapshot?.productType || '',
      planId: planSnapshot?.planId || '',
      lobCategory: planSnapshot?.lobCategory || '',
      subscriberName: getSubscriberName(coverage) || '',
      subscriberDob: getSubscriberDob(coverage),
      subscriberMemberId: getSubscriberMemberId(coverage) || '',
    })
    const matchedOrganization = organizations.find(
      (organization) =>
        organization.payerId === (coverage.payor?.[0]?.identifier?.value || '') &&
        organization.state === coverageState,
    )
    setSearchValue(matchedOrganization?.label || coverage.payor?.[0]?.display || '')
  }

  const getCoverageForRank = (rank: InsuranceRank): LocalCoverage | null => {
    const draftCoverage = drafts.draftCoverages[rank]
    if (draftCoverage) {
      return { type: 'pending', data: draftCoverage }
    }

    if (!patientId) {
      return null
    }

    const existingCoverage = selectCoverageForOrder(existingCoverages, getOrderFromRank(rank))
    if (existingCoverage) {
      return { type: 'existing', data: existingCoverage }
    }

    return null
  }

  function handleEditCoverage(rank: InsuranceRank) {
    setActiveInsuranceRank(rank)
    setLatestEligibilityResponse(null)
    setIsEligibilityStale(false)
    insuranceCards.resetInsuranceUploads()

    const draft = drafts.draftCoverages[rank]
    if (draft) {
      setEditingCoverage({ type: 'draft', data: draft })
      populateFormForDraft(draft)
      setIsModalOpen(true)
      return
    }

    if (patientId) {
      const coverage = selectCoverageForOrder(existingCoverages, getOrderFromRank(rank))
      if (coverage) {
        setEditingCoverage({ type: 'existing', data: coverage })
        populateFormForCoverage(coverage, rank)
        setIsModalOpen(true)
        return
      }
    }

    setEditingCoverage(null)
    form.reset()
    form.setFieldValue('insuranceRank', rank)
    setSearchValue('')
    setIsModalOpen(true)
  }

  // A request without a catalog payer id only names the payer as the patient typed it, so staff
  // confirm that the payer they picked is that one before the plan is saved and the request closed.
  const handleSaveAndCloseRequest = async (values: InsuranceFormData) => {
    if (!patientInsuranceRequest || patientInsuranceRequest.payerId) {
      return handleSave(values, patientInsuranceRequest)
    }
    const request = patientInsuranceRequest
    modals.openConfirmModal({
      title: 'Close the patient insurance request?',
      children: `Patient wrote: ${request.payerName}. You picked: ${values.payorName}. Close the request?`,
      labels: { confirm: 'Add plan & close request', cancel: 'Cancel' },
      onConfirm: () => void handleSave(values, request),
    })
  }

  const handleDeleteCoverage = (rank: InsuranceRank) => {
    modals.openConfirmModal({
      title: 'Delete Insurance Coverage',
      children: `Are you sure you want to delete ${rank} insurance coverage? This action cannot be undone.`,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        if (!patientId) {
          drafts.removeDraftCoverage(rank)
          notifications.show({
            title: 'Success',
            message: 'Insurance coverage removed',
            color: 'green',
          })
          return
        }

        deleteCoverage(patientId, rank)
          .then(() => {
            notifications.show({
              title: 'Success',
              message: 'Insurance coverage removed',
              color: 'green',
            })
          })
          .catch((error) => {
            notifications.show({
              title: 'Error',
              message: error.message || 'Unknown error',
              color: 'red',
            })
          })
      },
    })
  }

  const handleDeleteInsuranceCardImage = (side: InsuranceCardSide) => {
    const hasBackRemaining =
      Boolean(insuranceCards.backImageFile) || insuranceCards.backHasPersistedCard
    if (side === 'front' && hasBackRemaining) {
      notifications.show({
        title: 'Delete the back image first',
        message: 'The front of the insurance card is required while a back image is attached.',
        color: 'red',
      })
      return
    }

    const hasPendingSelection = Boolean(
      side === 'front' ? insuranceCards.frontImageFile : insuranceCards.backImageFile,
    )
    const hasPersistedDocument =
      !hasPendingSelection &&
      Boolean(patientId) &&
      Boolean(activeInsuranceRank) &&
      editingCoverage?.type === 'existing' &&
      Boolean(
        side === 'front'
          ? insuranceCards.frontHasPersistedCard
          : insuranceCards.backHasPersistedCard,
      )

    const performDelete = async () => {
      if (!hasPersistedDocument) {
        insuranceCards.handleInsuranceFileChange(side, null)
        return
      }

      try {
        await deleteInsuranceCardImage(
          patientId as string,
          activeInsuranceRank as InsuranceRank,
          side,
        )
        insuranceCards.updatePreviewForSide(side, null)
        insuranceCards.clearPersistedCard(side)
        notifications.show({ color: 'green', message: 'Insurance card image deleted.' })
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: (error as Error)?.message || 'Unable to delete insurance card image.',
          color: 'red',
        })
      }
    }

    modals.openConfirmModal({
      title: `Delete ${side === 'front' ? 'front' : 'back'} of insurance card?`,
      children: hasPersistedDocument
        ? 'This removes the uploaded image from the coverage.'
        : 'This clears the selected image.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void performDelete()
      },
    })
  }

  const handlePayerSelected = (option: {
    payerId: string
    payerName: string
    state?: string
    label: string
  }) => {
    setSearchValue(option.label)
    form.setFieldValue('payorId', option.payerId)
    form.setFieldValue('payorName', option.payerName)
    form.setFieldValue('coverageState', option.state)
    form.setFieldValue('planName', '')
    form.setFieldValue('productType', '')
    form.setFieldValue('planId', '')
    form.setFieldValue('lobCategory', '')
  }

  const handlePlanChange = (snapshot: InsurancePlanSnapshot | null) => {
    form.setFieldValue('planName', snapshot?.planName || '')
    form.setFieldValue('productType', snapshot?.productType || '')
    form.setFieldValue('planId', snapshot?.planId || '')
    form.setFieldValue('lobCategory', snapshot?.lobCategory || '')
  }

  const handleCoverageStateChange = (state: string | null) => {
    form.setFieldValue('coverageState', state || '')
  }

  const handleCustomPayerCreated = async (payer: {
    id: string
    payerId: string
    payerName: string
    state?: string
    source: 'default' | 'custom'
    editable: boolean
    deletable: boolean
    parameterId?: string
  }) => {
    await fetchOrganizations()
    handlePayerSelected({
      ...payer,
      state: payer.state || '',
      label: `${payer.payerName} - ${payer.state || ''}`,
    })
  }

  /**
   * `requestToClose` is the patient insurance request staff are adding the plan from. It is only
   * closed once the plan is saved, so a failed save leaves the request in the review queue. A plain
   * save never passes one: editing a coverage is not, by itself, a decision on the request.
   */
  const handleSave = async (
    values: InsuranceFormData,
    requestToClose?: PatientInsuranceRequest | null,
  ) => {
    setLoading(true)

    try {
      if (!patientId) {
        const existingDraft =
          editingCoverage?.type === 'draft'
            ? editingCoverage.data
            : drafts.draftCoverages[values.insuranceRank]
        const isValid = insuranceCards.validateCardSelection(
          existingDraft?.frontImageBase64,
          existingDraft?.backImageBase64,
        )
        if (!isValid) {
          return
        }
      }

      const frontBase64 = insuranceCards.frontImageFile
        ? await insuranceCards.fileToBase64(insuranceCards.frontImageFile)
        : undefined
      const backBase64 = insuranceCards.backImageFile
        ? await insuranceCards.fileToBase64(insuranceCards.backImageFile)
        : undefined
      const editingId =
        editingCoverage?.type === 'draft'
          ? editingCoverage.data.id
          : editingCoverage?.type === 'existing'
            ? editingCoverage.data.id
            : undefined

      const coverageTemplate = buildCoverageTemplateFromValues(
        values,
        editingId,
        frontBase64,
        backBase64,
      )

      if (patientId) {
        const existingCoverageId =
          editingCoverage?.type === 'existing' ? editingCoverage.data.id : undefined

        const savedCoverage = await upsertCoverageForPatient(patientId, coverageTemplate, {
          coverageId: existingCoverageId,
        })

        if (insuranceCards.hasNewInsuranceCardSelection) {
          const uploadedCoverage = await uploadInsuranceCardsForCoverage(
            patientId,
            savedCoverage,
            values.insuranceRank,
            {
              front: insuranceCards.frontImageFile || undefined,
              back: insuranceCards.backImageFile || undefined,
            },
            insuranceCards.validateCardSelection,
          )

          if (!uploadedCoverage) {
            return
          }
        }
      } else {
        drafts.setDraftCoverage(
          coverageTemplate,
          editingCoverage?.type === 'draft' ? editingCoverage.data.insuranceRank : undefined,
        )
      }

      notifications.show({
        title: 'Success',
        message: `Insurance coverage ${editingCoverage ? 'updated' : 'added'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      })

      if (patientId && requestToClose) {
        try {
          const result = await closePatientInsuranceRequest(medplum, requestToClose)
          if (result.outcome === 'changed') {
            notifications.show({
              title: 'Error',
              message: result.current
                ? 'The patient changed this request after you opened it. Review the updated request, then close it.'
                : // The re-read failed, so who changed it is unknown.
                  'This request changed after you opened it. Review the current request, then close it.',
              color: 'red',
              icon: <IconAlertCircle size={16} />,
            })
          } else {
            notifications.show({
              color: 'green',
              message:
                result.outcome === 'closed'
                  ? 'Patient insurance request closed.'
                  : // Someone else already closed it: the outcome staff wanted, not a failure.
                    'This request was already closed. The plan was saved.',
              icon: result.outcome === 'closed' ? undefined : <IconCheck size={16} />,
            })
          }
        } catch (error) {
          logError('insurance', 'Error closing patient insurance request', error)
          notifications.show({
            title: 'Error',
            message:
              'Insurance was saved, but the patient insurance request could not be closed. Dismiss it manually.',
            color: 'red',
            icon: <IconAlertCircle size={16} />,
          })
        }
      }

      closeEditor()
      void refreshPatientInsuranceRequest()
      onCoverageSaved?.()
    } catch (error) {
      logError('insurance', 'Error saving coverage', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to save insurance coverage',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCheckInsurance = async () => {
    setIsEligibilityCheck(true)

    try {
      if (!patientId) {
        throw new Error('Missing patient')
      }
      if (editingCoverage?.type !== 'existing' || !editingCoverage.data.id) {
        throw new Error('Save the insurance coverage before checking eligibility')
      }

      const { result } = await checkInsurance(patientId, editingCoverage.data.id)

      updateEligibilityState(editingCoverage.data, result)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: (error as Error)?.message || 'Something went wrong',
        color: 'red',
      })
    } finally {
      setIsEligibilityCheck(false)
    }
  }

  useEffect(() => {
    if (!isModalOpen || !patientId || !activeInsuranceRank) {
      return
    }

    const coverage = selectCoverageForOrder(
      existingCoverages,
      getOrderFromRank(activeInsuranceRank),
    )
    if (!coverage) {
      return
    }

    let disposed = false

    const loadImages = async () => {
      await insuranceCards.loadExistingCoverageImages(medplum, coverage)
      if (disposed) {
        return
      }
    }

    void loadImages()

    return () => {
      disposed = true
    }
  }, [activeInsuranceRank, existingCoverages, insuranceCards, isModalOpen, medplum, patientId])

  useEffect(() => {
    if (
      !isModalOpen ||
      !patientId ||
      editingCoverage?.type !== 'existing' ||
      !editingCoverage.data.id
    ) {
      setLatestEligibilityResponse(null)
      setIsEligibilityStale(false)
      return
    }

    let disposed = false

    const loadLatestEligibility = async () => {
      try {
        const response = await fetchLatestEligibilityResponseForCoverage(
          patientId,
          editingCoverage.data.id as string,
        )

        if (!disposed) {
          updateEligibilityState(editingCoverage.data, response)
        }
      } catch (error) {
        if (!disposed) {
          setLatestEligibilityResponse(null)
          setIsEligibilityStale(false)
        }
        logError('insurance', 'Error loading latest eligibility response', error)
      }
    }

    void loadLatestEligibility()

    return () => {
      disposed = true
    }
  }, [editingCoverage, fetchLatestEligibilityResponseForCoverage, isModalOpen, patientId])

  const refreshPatientInsuranceRequest = async () => {
    if (!patientId) {
      setPatientInsuranceRequest(null)
      return
    }

    try {
      setPatientInsuranceRequest(await fetchLatestPatientInsuranceRequest(medplum, patientId))
    } catch {
      setPatientInsuranceRequest(null)
    }
  }

  useEffect(() => {
    if (!isModalOpen || !patientId) {
      return
    }

    void refreshPatientInsuranceRequest()
  }, [isModalOpen, patientId, medplum])

  useEffect(() => {
    if (!registerSaveCoverages) {
      return
    }

    const hasDrafts = Object.values(drafts.draftCoverages).some(Boolean)
    if (patientId || !hasDrafts) {
      registerSaveCoverages(null)
      return () => registerSaveCoverages(null)
    }

    registerSaveCoverages((createdPatientId: string) =>
      drafts.persistDraftCoverages(createdPatientId),
    )
    return () => registerSaveCoverages(null)
  }, [drafts, patientId, registerSaveCoverages])

  useEffect(() => {
    if (!initialInsuranceRank) {
      return
    }

    if (patientId && !hasFetchedCoverages) {
      return
    }

    handleEditCoverage(initialInsuranceRank)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedCoverages, initialInsuranceRank, patientId])

  const organizationOptions: Array<{
    id: string
    payerId: string
    payerName: string
    state: string | undefined
    source: 'default' | 'custom'
    editable: boolean
    deletable: boolean
    parameterId?: string
    label: string
  }> = organizations.map((organization) => ({
    id: organization.id,
    payerId: organization.payerId,
    payerName: organization.payerName,
    state: organization.state,
    source: organization.source,
    editable: organization.editable,
    deletable: organization.deletable,
    parameterId: organization.parameterId,
    label: organization.label || 'Unknown Organization',
  }))

  const filteredOptions = organizationOptions
    .filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    .map((option) => option.label)
  const selectedPayerOption =
    organizationOptions.find(
      (option) =>
        option.payerId === form.values.payorId && option.state === form.values.coverageState,
    ) ?? null
  const isCoverageStateLocked = Boolean(selectedPayerOption)
  const insurancePlansStateProp =
    isCoverageStateLocked || (Boolean(editingCoverage) && Boolean(form.values.coverageState))
      ? form.values.coverageState || null
      : null

  const historicalRecordsCount = patientId
    ? existingCoverages.filter((coverage) => coverage.status !== 'active').length
    : 0

  return (
    <div>
      {/* Section header: label + rule, with the historical-records count trailing. */}
      <div className="mb-3 flex items-center gap-3">
        <span className="text-[11px] font-bold tracking-[0.08em] text-[#8A929C] uppercase">
          Insurance
        </span>
        <span className="h-px flex-1 bg-[#EEF0F2]" />
        <span className="shrink-0 text-[12.5px] font-semibold text-[#4A5160]">
          Historical records
        </span>
        <span className="shrink-0 rounded-full bg-[#F1F3F5] px-2 py-[1px] text-[11px] font-bold text-[#697386]">
          {historicalRecordsCount}
        </span>
      </div>

      {/* One bordered list; each rank is a row (see ManageCoverageCard). */}
      <div className="divide-y divide-[#EEF0F2] overflow-hidden rounded-xl border border-[#E7E9EC]">
        {INSURANCE_RANKS.map((item) => {
          const rank = item.value as InsuranceRank
          const coverage = getCoverageForRank(rank)

          return (
            <ManageCoverageCard
              key={rank}
              type={rank}
              coverage={coverage}
              onEdit={() => handleEditCoverage(rank)}
              onDelete={() => handleDeleteCoverage(rank)}
              getInsuranceDisplayData={getInsuranceDisplayData}
            />
          )
        })}
      </div>

      <ManageCoveragesEditorModal
        editingCoverage={editingCoverage}
        form={form}
        frontInputRef={insuranceCards.frontInputRef}
        backInputRef={insuranceCards.backInputRef}
        frontPreviewUrl={insuranceCards.frontPreviewUrl}
        backPreviewUrl={insuranceCards.backPreviewUrl}
        frontHasPersistedCard={insuranceCards.frontHasPersistedCard}
        backHasPersistedCard={insuranceCards.backHasPersistedCard}
        hasAnyInsurancePreview={insuranceCards.hasAnyInsurancePreview}
        hasNewInsuranceCardSelection={insuranceCards.hasNewInsuranceCardSelection}
        isEligibilityCheck={isEligibilityCheck}
        loading={loading}
        opened={isModalOpen}
        organizationOptions={organizationOptions}
        filteredOptions={filteredOptions}
        searchValue={searchValue}
        isCoverageStateLocked={isCoverageStateLocked}
        insurancePlansStateProp={insurancePlansStateProp}
        latestEligibilityResponse={latestEligibilityResponse}
        isEligibilityStale={isEligibilityStale}
        patientInsuranceRequest={patientInsuranceRequest}
        patientId={patientId}
        onPatientInsuranceRequestDismissed={() => {
          void refreshPatientInsuranceRequest()
          onPatientInsuranceRequestDismissed?.()
        }}
        onClose={closeEditor}
        onSubmit={(values) => handleSave(values)}
        onSubmitAndCloseRequest={handleSaveAndCloseRequest}
        onSearchChange={setSearchValue}
        onPayerSelected={handlePayerSelected}
        onOpenCreatePayer={() => setIsCreatePayerModalOpen(true)}
        onPlanChange={handlePlanChange}
        onCoverageStateChange={handleCoverageStateChange}
        onStartInsuranceSelection={() =>
          insuranceCards.handleStartInsuranceSelection(activeInsuranceRank)
        }
        onRetakeImage={(side) => insuranceCards.handleRetakeImage(side, activeInsuranceRank)}
        onDeleteImage={handleDeleteInsuranceCardImage}
        onFrontInputChange={insuranceCards.handleFrontInputChange}
        onBackInputChange={insuranceCards.handleBackInputChange}
        onDropImage={insuranceCards.handleInsuranceFileChange}
        onZoomImage={insuranceCards.setZoomedImageUrl}
        onResetInsuranceUploads={insuranceCards.resetInsuranceUploads}
        onCheckInsurance={handleCheckInsurance}
      />

      <CustomInsurancePayerModal
        opened={isCreatePayerModalOpen}
        onClose={() => setIsCreatePayerModalOpen(false)}
        onSuccess={(payer) => {
          void handleCustomPayerCreated(payer)
          setIsCreatePayerModalOpen(false)
        }}
      />

      <ImageZoomModal
        opened={!!insuranceCards.zoomedImageUrl}
        imageUrl={insuranceCards.zoomedImageUrl}
        title="Insurance Card Preview"
        imageAlt="Insurance card"
        onClose={() => insuranceCards.setZoomedImageUrl(null)}
      />
    </div>
  )
}
